/**
 * SurveyCarousel - Displays surveys in a horizontal carousel
 * Similar to ProductGroupCarousel but for survey cards
 */
import { useState, useEffect } from 'react';
import { SurveyCard } from '@brandclub/bc-widgets';
import StoreCarousel from '../StoreCarousel';
import { ConciergeSurveyData } from '../api';

export interface SurveyCarouselProps {
  /** Array of surveys to display */
  surveys: ConciergeSurveyData[];
  /** Loading state */
  isLoading?: boolean;
  /** Callback when a survey is clicked */
  onSurveyClick?: (survey: ConciergeSurveyData) => void;
}

export const SurveyCarousel: React.FC<SurveyCarouselProps> = ({
  surveys,
  isLoading = false,
  onSurveyClick,
}) => {
  // Determine mobile view based on viewport width
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 500;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 500);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Card width based on viewport
  const cardWidth = isMobileView ? 240 : 280;

  // Show loading skeletons if loading and no surveys
  if (isLoading && surveys.length === 0) {
    return (
      <div className="tw:w-full tw:my-4">
        <StoreCarousel>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                width: cardWidth,
                height: 280,
                borderRadius: 16,
                backgroundColor: '#f3f4f6',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
          ))}
        </StoreCarousel>
      </div>
    );
  }

  // Don't render if no surveys
  if (!surveys || surveys.length === 0) {
    return null;
  }

  return (
    <div className="tw:w-full tw:my-4" style={{ padding: '8px 0' }}>
      <StoreCarousel gap={16}>
        {surveys.map((survey) => (
          <div key={survey.campaignId} style={{ padding: '4px' }}>
            <SurveyCard
              survey={{
                campaignId: survey.campaignId,
                title: survey.title,
                brandName: survey.brandName,
                brandId: survey.brandId,
                brandLogoUrl: survey.brandLogoUrl,
                description: survey.description,
                rewardAmount: survey.rewardAmount,
                previewImageUrl: survey.previewImageUrl,
              }}
              width={cardWidth}
              onClick={
                onSurveyClick
                  ? () => onSurveyClick(survey)
                  : () => window.open(`/survey/${survey.campaignId}`, '_blank', 'noopener')
              }
            />
          </div>
        ))}
      </StoreCarousel>
    </div>
  );
};

export default SurveyCarousel;
